import { AsyncTaskStatus, AsyncTaskType } from '@/types/catalogs/AsyncTask'

interface TaskStorage {
  id: number
  type: AsyncTaskType
  updateDate: string
}
/** This class will help to represent the user info with its taskIds into the localStorage */
class UserTasks {
  userId: number
  tasks: TaskStorage[]

  static getInstance (userId: number = -1, tasks: TaskStorage[] = []): UserTasks {
    const userTask = new UserTasks()
    userTask.userId = userId
    userTask.tasks = tasks
    return userTask
  }

  addTask (task: TaskStorage): void {
    const taskIndex = this.tasks.findIndex(current => current.id === task.id)
    if (taskIndex >= 0) this.tasks.splice(taskIndex, 1, task)
    else this.tasks.unshift(task)
  }

  removeTask (taskId: number) {
    const taskIndex = this.tasks.findIndex(current => current.id === taskId)
    if (taskIndex >= 0) this.tasks.splice(taskIndex, 1)
  }
}

/** To save the async task ids into localStorage browser */
/**
 * This will have the form
 * task_notificacions: [
 *  {
  *  state: <AsyncTaskStatus>, // NEW, WAITING, PROCESSING, DONE, ERROR, CANCELED
  *  users: [
  *    {
  *      id: <userId>,
  *      tasks: [<taskIds>]
  *    }
  *  ]
  *  ...
 *  },
 *  ...
 * ]
 */

interface NotificationList {
  state: AsyncTaskStatus
  users: UserTasks[]
}

const STORAGE_TAG: string = 'task_notifications'
class AsyncTasksStorage {
  private storage: NotificationList[]

  constructor () {
    this.storage = JSON.parse(localStorage.getItem(STORAGE_TAG) || '[]')
  }

  existListByState (state: AsyncTaskStatus): boolean {
    return this.storage.some((list: NotificationList) => list.state === state)
  }

  addTaskToUserByState (state: AsyncTaskStatus, userId: number, task: TaskStorage): void {
    const userTask = this.getUserTasksByState(state, userId)
    userTask.addTask(task)
    this.updateUserTasksByState(state, userTask.userId, userTask.tasks)
  }

  removeUserTaskByState (state: AsyncTaskStatus, userId: number, taskId: number): void {
    const userTasks = this.getUserTasksByState(state, userId)
    userTasks.removeTask(taskId)
    this.updateUserTasksByState(state, userTasks.userId, userTasks.tasks)
  }

  getNotificationListByState (state: AsyncTaskStatus) : NotificationList {
    const list = this.storage.find((list: NotificationList) => list.state === state)
    if (!list) this.storage.push({ state, users: [] })
    return list ?? this.storage[this.storage.length - 1]
  }

  getUserTasksByState (state: AsyncTaskStatus, userId: number) : UserTasks {
    const users: UserTasks[] = this.getNotificationListByState(state).users
    const user = users.find(userTask => userTask.userId === userId)
    if (user) return UserTasks.getInstance(user.userId, user.tasks)
    else return UserTasks.getInstance(userId, [])
  }

  saveStorage () {
    localStorage.setItem(STORAGE_TAG, JSON.stringify(this.storage))
  }

  updateUserTasksByState (state: AsyncTaskStatus, userId: number, userTasks: TaskStorage[]) {
    const list = this.getNotificationListByState(state)
    const users = list.users
    const user = users.find(user => user.userId === userId)
    if (user) user.tasks = userTasks
    else users.push(UserTasks.getInstance(userId, userTasks))
    this.saveStorage()
  }
}

export default new AsyncTasksStorage()
